import { faHeart } from "@fortawesome/free-regular-svg-icons";
import {
  faCheck,
  faChevronLeft,
  faChevronRight,
  faCircle,
  faClock,
  faCopy,
  faDatabase,
  faEllipsisV,
  faPen,
  faRotateLeft,
  faSpinner,
  faTrash,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DevToolbar from "../components/DevToolbar";
import { Breadcrumb, SearchInput, SortButtons, ToggleButton, FilterCount, Loading } from "../components/ui";
import {
  getFromCache,
  setInCache,
  invalidateCache,
  CACHE_TTL,
  CACHE_KEYS,
} from "../utils/cacheUtils";

const ITEMS_PER_PAGE = 20;

const formatDuration = (startedAt, completedAt) => {
  if (!startedAt) return null;

  const start = new Date(startedAt);
  const end = completedAt ? new Date(completedAt) : new Date();
  const diffMs = end - start;

  if (diffMs < 0) return null;

  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  } else {
    return `${seconds}s`;
  }
};

const Projects = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [sortColumn, setSortColumn] = useState("timestamp");
  const [sortDirection, setSortDirection] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [showTestProjects, setShowTestProjects] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [clearConfirm, setClearConfirm] = useState(null);
  const host = process.env.REACT_APP_SERVER;

  const fetchData = async (forceRefresh = false) => {
    // Check cache first (unless forcing refresh)
    if (!forceRefresh) {
      const cachedProjects = getFromCache(CACHE_KEYS.projectsList(), CACHE_TTL.PROJECTS_LIST);
      if (cachedProjects) {
        console.log("⚡ Using cached projects list");
        setData(cachedProjects);
        return;
      }
    }

    setLoading(true);
    try {
      const response = await fetch(`${host}/api/projects`);
      const jsonData = await response.json();
      jsonData.data.projects.sort((a, b) => b.id - a.id);

      // Cache the projects list
      setInCache(CACHE_KEYS.projectsList(), jsonData.data.projects);

      setData(jsonData.data.projects);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDeleteProject = async (projectId) => {
    try {
      const response = await fetch(`${host}/api/projects/${projectId}`, {
        method: "DELETE",
      });
      if (response.ok || response.status === 204) {
        // Invalidate caches
        invalidateCache(CACHE_KEYS.projectsList());
        invalidateCache(CACHE_KEYS.projectDetail(projectId));

        setData(prev => prev.filter(p => p.id !== projectId));
        setDeleteConfirm(null);
      }
    } catch (error) {
      console.error("Failed to delete project:", error);
    }
  };

  const handleCopyProject = async (project) => {
    try {
      const response = await fetch(`${host}/api/projects`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: `${project.name} (Copy)`,
          client: project.client,
          category: project.category,
          description: project.description,
          brand_strategy: project.brand_strategy,
          attributes: project.attributes,
          max_chars: project.max_chars,
          special_chars: project.special_chars,
          local_spelling: project.local_spelling,
          countries: project.countries,
          nice_classes: project.nice_classes,
          status: "draft",
          differentiators: project.differentiators,
          test_project: project.test_project,
        }),
      });
      if (response.ok) {
        // Invalidate projects list cache
        invalidateCache(CACHE_KEYS.projectsList());

        const result = await response.json();
        setData(prev => [result.data.project, ...prev]);
      }
    } catch (error) {
      console.error("Failed to copy project:", error);
    }
  };

  const handleClearProject = async (projectId) => {
    try {
      const response = await fetch(`${host}/api/projects/${projectId}/clear`, {
        method: "DELETE",
      });
      if (response.ok || response.status === 204) {
        // Invalidate caches
        invalidateCache(CACHE_KEYS.projectsList());
        invalidateCache(CACHE_KEYS.projectDetail(projectId));

        // Update the project in local state to reflect cleared status
        setData(prev => prev.map(p =>
          p.id === projectId
            ? { ...p, status: 'created', names_count: 0 }
            : p
        ));
        setClearConfirm(null);
      }
    } catch (error) {
      console.error("Failed to clear project:", error);
    }
  };

  // Status priority for sorting (lower = higher priority)
  const getStatusPriority = status => {
    if (!status) return 4; // Draft
    const s = status.toString().toLowerCase().trim();
    if (["generate", "generating", "mutate", "mutating"].includes(s)) return 0; // Processing
    if (["completed", "complete"].includes(s)) return 1; // Review
    if (s === "done") return 2; // Done
    if (["incomplete", "failed", "stopped"].includes(s)) return 3; // Stopped
    return 4; // Draft
  };

  const compareValues = (valueA, valueB, column) => {
    if (valueA === null) return sortDirection === "asc" ? 1 : -1;
    if (valueB === null) return sortDirection === "asc" ? -1 : 1;

    // Special handling for status column
    if (column === "status") {
      const priorityA = getStatusPriority(valueA);
      const priorityB = getStatusPriority(valueB);
      return sortDirection === "asc" ? priorityA - priorityB : priorityB - priorityA;
    }

    if (typeof valueA === "string" && typeof valueB === "string") {
      const trademarkOrder = ["A", "B", "C", "D", "F"];
      const indexA = trademarkOrder.indexOf(valueA);
      const indexB = trademarkOrder.indexOf(valueB);

      if (indexA !== -1 && indexB !== -1) {
        return sortDirection === "asc" ? indexA - indexB : indexB - indexA;
      }
      return sortDirection === "asc" ? valueA.localeCompare(valueB) : valueB.localeCompare(valueA);
    }
    return sortDirection === "asc" ? valueA - valueB : valueB - valueA;
  };

  // Filter by search query and test project flag
  const filteredData = data.filter(project => {
    // Filter out test projects unless toggle is on
    if (!showTestProjects && project.test_project) return false;

    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      project.name?.toLowerCase().includes(query) ||
      project.client?.toLowerCase().includes(query)
    );
  });

  // Count test projects for the toggle label
  const testProjectCount = data.filter(p => p.test_project).length;

  const sortedData = [...filteredData].sort((a, b) => {
    if (sortColumn) {
      const valueA = a[sortColumn];
      const valueB = b[sortColumn];
      return compareValues(valueA, valueB, sortColumn);
    }
    return 0;
  });

  // Grouping helpers for dividers
  const getGroupKey = (project, column) => {
    switch (column) {
      case "timestamp": {
        const date = new Date(project.timestamp);
        return `${date.getFullYear()}-${String(date.getMonth()).padStart(2, '0')}`;
      }
      case "name": {
        const firstChar = (project.name || "").charAt(0).toUpperCase();
        return /[A-Z]/.test(firstChar) ? firstChar : "#";
      }
      case "status": {
        return getStatusPriority(project.status);
      }
      default:
        return "all";
    }
  };

  const getGroupLabel = (project, column) => {
    switch (column) {
      case "timestamp": {
        const date = new Date(project.timestamp);
        return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
      }
      case "name": {
        const firstChar = (project.name || "").charAt(0).toUpperCase();
        return /[A-Z]/.test(firstChar) ? firstChar : "#";
      }
      case "status": {
        const priority = getStatusPriority(project.status);
        const labels = ["Processing", "Review", "Done", "Stopped", "Draft"];
        return labels[priority] || "Draft";
      }
      default:
        return "";
    }
  };

  const groupProjects = (projects, column) => {
    const groups = [];
    let currentGroup = null;
    let currentProjects = [];

    projects.forEach(project => {
      const groupKey = getGroupKey(project, column);
      if (groupKey !== currentGroup) {
        if (currentProjects.length > 0) {
          groups.push({ key: currentGroup, label: getGroupLabel(currentProjects[0], column), projects: currentProjects });
        }
        currentGroup = groupKey;
        currentProjects = [project];
      } else {
        currentProjects.push(project);
      }
    });
    if (currentProjects.length > 0) {
      groups.push({ key: currentGroup, label: getGroupLabel(currentProjects[0], column), projects: currentProjects });
    }
    return groups;
  };

  // Pagination
  const totalPages = Math.ceil(sortedData.length / ITEMS_PER_PAGE);
  const paginatedData = sortedData.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const formatDate = dateString => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
  };

  const getStatusInfo = status => {
    if (!status) return { icon: faCircle, color: "text-base-content/30", label: "Draft" };
    const s = status.toString().toLowerCase().trim();
    if (s === "done")
      return { icon: faCheck, color: "text-success", label: "Complete" };
    if (["completed", "complete"].includes(s))
      return { icon: faCircle, color: "text-info", label: "Review" };
    if (["generate", "generating", "mutate", "mutating"].includes(s))
      return { icon: faSpinner, color: "text-primary", spin: true, label: "Processing" };
    if (["incomplete", "failed", "stopped"].includes(s))
      return { icon: faXmark, color: "text-error", label: "Stopped" };
    return { icon: faCircle, color: "text-base-content/30", label: "Draft" };
  };

  const navigate = useNavigate();

  const ProjectTile = ({ project, onCopy, onClear, onDelete }) => {
    const status = getStatusInfo(project.status);
    const [showMenu, setShowMenu] = useState(false);
    const [liveElapsed, setLiveElapsed] = useState(null);

    const isGenerating = ["generate", "generating", "mutate", "mutating"].includes(
      project.status?.toString().toLowerCase().trim()
    );

    // Live timer for generating projects
    useEffect(() => {
      if (!isGenerating || !project.generation_started_at) {
        setLiveElapsed(null);
        return;
      }

      const updateElapsed = () => {
        setLiveElapsed(formatDuration(project.generation_started_at, null));
      };

      updateElapsed();
      const interval = setInterval(updateElapsed, 1000);
      return () => clearInterval(interval);
    }, [isGenerating, project.generation_started_at]);

    return (
      <div className={`relative bg-base-100 rounded-lg p-4 sm:p-5 border-2 border-transparent hover:border-primary/50 transition-colors h-full flex flex-col min-h-[160px] sm:min-h-[180px] ${project.test_project ? "opacity-60" : ""}`}>
        {/* Menu Button - Top Right */}
        <div className="absolute top-2 right-2 z-10">
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowMenu(!showMenu); }}
            className="w-7 h-7 flex items-center justify-center rounded-full text-base-content/30 hover:text-base-content/70 hover:bg-base-content/10 transition-colors"
          >
            <FontAwesomeIcon icon={faEllipsisV} className="text-xs" />
          </button>

          {/* Dropdown Menu */}
          {showMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
              <div className="absolute right-0 top-8 bg-base-100 rounded-xl shadow-md py-2 min-w-[140px] z-20 border border-base-content/5">
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowMenu(false); navigate(`/projects/${project.id}/edit`); }}
                  className="w-full px-4 py-2.5 text-left text-sm text-base-content/70 hover:bg-base-content/5 flex items-center gap-3 transition-colors"
                >
                  <FontAwesomeIcon icon={faPen} className="text-sm text-base-content/40 w-4" />
                  Edit
                </button>
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowMenu(false); onCopy(project); }}
                  className="w-full px-4 py-2.5 text-left text-sm text-base-content/70 hover:bg-base-content/5 flex items-center gap-3 transition-colors"
                >
                  <FontAwesomeIcon icon={faCopy} className="text-sm text-base-content/40 w-4" />
                  Copy
                </button>
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowMenu(false); onClear(project); }}
                  className="w-full px-4 py-2.5 text-left text-sm text-base-content/70 hover:bg-base-content/5 flex items-center gap-3 transition-colors"
                >
                  <FontAwesomeIcon icon={faRotateLeft} className="text-sm text-base-content/40 w-4" />
                  Clear
                </button>
                <button
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowMenu(false); onDelete(project); }}
                  className="w-full px-4 py-2.5 text-left text-sm text-base-content/70 hover:bg-error/5 hover:text-error flex items-center gap-3 transition-colors"
                >
                  <FontAwesomeIcon icon={faTrash} className="text-sm text-base-content/40 w-4" />
                  Delete
                </button>
              </div>
            </>
          )}
        </div>

        {/* Clickable Link Area */}
        <Link to={`/projects/${project.id}`} className="flex flex-col h-full">
          {/* Header: Title + Status */}
          <div className="flex items-start justify-between gap-2 mb-2 pr-6">
            <div className="flex items-start gap-2">
              <span className="block font-[Oswald] font-semibold tracking-tight uppercase leading-tight -skew-x-6 text-[1.5rem] sm:text-[2rem] text-[#555] line-clamp-2">
                {project.name}
              </span>
              {project.test_project && (
                <span className="text-[9px] px-1.5 py-0.5 bg-warning/20 text-warning rounded font-medium uppercase mt-1">
                  Test
                </span>
              )}
            </div>
            <FontAwesomeIcon
              icon={status.icon}
              className={`${status.color} text-xs mt-1 sm:mt-2 flex-shrink-0`}
              spin={status.spin}
              title={status.label}
            />
          </div>

          {/* Client */}
          <span className="block text-sm font-light italic text-[#666] mb-auto">{project.client || "—"}</span>

          {/* Footer: Date + Metrics */}
          <div className="mt-3 sm:mt-4 pt-2 sm:pt-3 border-t border-accent/10">
            <div className="flex items-center justify-between text-[10px] sm:text-xs text-base-content/50">
              <span className="font-mono">{formatDate(project.timestamp)}</span>
              <div className="flex items-center gap-2 sm:gap-3">
                {/* Duration - only show for actively generating projects */}
                {isGenerating && liveElapsed && (
                  <span className="flex items-center gap-1 text-primary font-medium">
                    <FontAwesomeIcon icon={faClock} className="text-[8px] sm:text-[10px]" />
                    {liveElapsed}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <FontAwesomeIcon icon={faHeart} className="text-[8px] sm:text-[10px]" />
                  {project.bookmarked_percent}%
                </span>
                <span className="flex items-center gap-1">
                  <FontAwesomeIcon icon={faDatabase} className="text-[8px] sm:text-[10px]" />
                  {project.names_count?.toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </Link>
      </div>
    );
  };


  if (loading) {
    return <Loading size="lg" message="Loading projects..." fullScreen />;
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <DevToolbar />
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Projects" },
        ]}
        className="mb-4"
      />

      {/* Header Card */}
      <div className="bg-base-100 rounded-lg p-4 sm:p-5 mb-6 border border-base-content/10">
        {/* Title + Button */}
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-semibold text-base-content">Projects</h1>
          <Link
            to="/projects/new"
            className="flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-content rounded-full text-xs font-medium uppercase tracking-wider"
          >
            <span className="text-primary-content/70">+</span>
            New Project
          </Link>
        </div>

        {/* Search */}
        <SearchInput
          value={searchQuery}
          onChange={value => { setSearchQuery(value); setCurrentPage(1); }}
          placeholder="Search projects..."
          className="mb-4"
        />

        {/* Sort + Count */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <SortButtons
              options={[
                { key: "timestamp", label: "Date" },
                { key: "name", label: "Name" },
                { key: "status", label: "Status" },
              ]}
              value={sortColumn}
              direction={sortDirection}
              onChange={(key, dir) => { setSortColumn(key); setSortDirection(dir); setCurrentPage(1); }}
            />
            {testProjectCount > 0 && (
              <ToggleButton
                active={showTestProjects}
                onChange={val => { setShowTestProjects(val); setCurrentPage(1); }}
                label={`Show test (${testProjectCount})`}
                activeLabel={`Hide test (${testProjectCount})`}
              />
            )}
          </div>
          <FilterCount filtered={filteredData.length} total={data.length} label="projects" />
        </div>
      </div>

      {/* Project Tiles */}
      {paginatedData.length > 0 ? (
        groupProjects(paginatedData, sortColumn).map((group, idx) => (
          <div key={group.key} className={idx > 0 ? "mt-6" : ""}>
            <div className="mb-3">
              <span className="text-sm font-medium text-base-content/60">{group.label}</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
              {group.projects.map(project => (
                <ProjectTile
                  key={project.id}
                  project={project}
                  onCopy={handleCopyProject}
                  onClear={setClearConfirm}
                  onDelete={setDeleteConfirm}
                />
              ))}
            </div>
          </div>
        ))
      ) : (
        <div className="text-center py-8 text-base-content/50">
          No projects found
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-6">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="p-2 rounded disabled:opacity-30"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <span className="text-sm text-base-content/60">
            {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="p-2 rounded disabled:opacity-30"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <DeleteConfirmModal
          project={deleteConfirm}
          onClose={() => setDeleteConfirm(null)}
          onConfirm={() => handleDeleteProject(deleteConfirm.id)}
        />
      )}

      {/* Clear Confirmation Modal */}
      {clearConfirm && (
        <ClearConfirmModal
          project={clearConfirm}
          onClose={() => setClearConfirm(null)}
          onConfirm={() => handleClearProject(clearConfirm.id)}
        />
      )}
    </div>
  );
};

// Delete Confirmation Modal Component
const DeleteConfirmModal = ({ project, onClose, onConfirm }) => {
  const [confirmText, setConfirmText] = useState("");
  const canDelete = confirmText.toLowerCase() === project.name.toLowerCase();

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-base-100 rounded-xl w-full max-w-md shadow-lg border border-base-content/5">
        {/* Header */}
        <div className="flex items-center justify-between p-5 pb-0">
          <h3 className="text-base font-semibold text-base-content">Delete Project</h3>
          <button onClick={onClose} className="text-base-content/30 hover:text-base-content/70 transition-colors">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          <p className="text-sm text-base-content/60">
            This will permanently delete <strong className="text-base-content/80">{project.name}</strong> and all its {project.names_count?.toLocaleString() || 0} names.
          </p>

          <div>
            <label className="block text-sm text-base-content/50 mb-2">
              Type <span className="font-medium text-base-content/70">{project.name}</span> to confirm
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="w-full px-3 py-2.5 bg-base-100 border border-base-content/10 rounded-lg text-sm focus:outline-none focus:border-base-content/30 transition-colors"
              placeholder={project.name}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-base-content/50 hover:text-base-content/70 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={!canDelete}
              className="px-5 py-2 bg-base-content text-base-100 rounded-full text-sm font-medium disabled:opacity-30 disabled:cursor-not-allowed hover:bg-base-content/80 transition-colors"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Clear Confirmation Modal Component
const ClearConfirmModal = ({ project, onClose, onConfirm }) => {
  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-base-100 rounded-xl w-full max-w-md shadow-lg border border-base-content/5">
        {/* Header */}
        <div className="flex items-center justify-between p-5 pb-0">
          <h3 className="text-base font-semibold text-base-content">Clear Project</h3>
          <button onClick={onClose} className="text-base-content/30 hover:text-base-content/70 transition-colors">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          <p className="text-sm text-base-content/60">
            This will delete all {project.names_count?.toLocaleString() || 0} generated names from <strong className="text-base-content/80">{project.name}</strong> and reset it to a fresh state.
          </p>
          <p className="text-sm text-base-content/60">
            The project settings will be preserved.
          </p>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-base-content/50 hover:text-base-content/70 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="px-5 py-2 bg-primary text-primary-content rounded-full text-sm font-medium hover:bg-primary/80 transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Projects;
